import { useFormikContext } from 'formik';

import {
  getDocumentName,
  graphql,
  ResolvableModeEnum,
} from '@e-socna/data-layer';
import { EMPTY_OBJECT } from '@e-socna/empty';

import { ResolvableMissionTypePage_OneResolvableMissionType_QueryDocument } from '../../../pages/ResolvableMissionType/hooks/useFindOneResolvableMissionTypeQuery.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useGraphQLQuery } from '../../../hooks/useGraphQLQuery.hook';

import { ResolvableServiceUserFormStep } from '../../ResolvableServiceUser';
import type { Props } from '../../ResolvableServiceUser/types/props.type';

import { useCreateMissionServiceUsageMutation } from '../hooks/useCreateResolvableMissionTypeServiceUsageMutation.hook';
import { useRemoveResolvableMissionTypeServiceUsageMutation } from '../hooks/useRemoveResolvableMissionTypeServiceUsageMutation.hook';
import { useUpdateResolvableMissionTypeServiceUsageMutation } from '../hooks/useUpdateResolvableMissionTypeServiceUsageMutation.hook';
import { useCreateResolvableMissionTypeServiceUsagesGroupMutation } from '../hooks/useCreateResolvableMissionTypeServiceUsagesGroupMutation.hook';
import { useRemoveResolvableMissionTypeServiceUsagesGroupMutation } from '../hooks/useRemoveResolvableMissionTypeServiceUsagesGroupMutation.hook';
import { useUpdateResolvableMissionTypeServiceUsagesGroupMutation } from '../hooks/useUpdateResolvableMissionTypeServiceUsagesGroupMutation.hook';
import { useReorderResolvableMissionTypeServiceUsagesGroupsMutation } from '../hooks/useReorderResolvableMissionTypeServiceUsagesGroupsMutation.hook';
import { useSwitchResolvableMissionTypeGroupingModeMutation } from '../hooks/useSwitchResolvableMissionTypeGroupingModeMutation.hook';
import { useMoveResolvableMissionTypeServiceUsageToGroupMutation } from '../hooks/useMoveResolvableMissionTypeServiceUsageToGroupMutation.hook';
import { useSwitchResolvableMissionTypeTotalModeMutation } from '../hooks/useSwitchResolvableMissionTypeTotalModeMutation.hook';
import { useUpdateResolvableMissionTypeManualTotalMutation } from '../hooks/useUpdateResolvableMissionTypeManualTotalMutation.hook';
import { resolvableMissionTypeForm2ApiMapper } from '../mappers/resolvable-mission-type.form2api.mapper';
import type { ResolvableMissionTypeForm_ResolvableMissionType } from '../fragments/resolvable-mission-type.fragment';

export const ResolvableMissionTypeForm_ServiceUsages_TotalCalculation_QueryDocument =
  graphql(`
    query ResolvableMissionTypeForm_ServiceUsages_TotalCalculation_Query(
      $doc: ResolvableMissionTypeInput!
      $businessContext: BusinessContextInput
    ) {
      calculateResolvableMissionTypeTotal(
        doc: $doc
        businessContext: $businessContext
      ) {
        ...ServiceUsagesTotalCardCmp_TotalCalculation_Fragment
      }
    }
  `);

export const ResolvableMissionTypeServiceUsagesFormStep = ({
  fallbackResolvableServiceUsagesGroups,
  groupTotalCalculations,
  id,
  paths,
  totalCalculation,
  useDirectMutation,
}: Omit<
  Props,
  | 'documentResolversSettings'
  | 'mutations'
  | 'queryKeysToInvalidate'
  | 'resolvableFieldSettings'
  | 'storage'
>) => {
  const {
    fileDirectoryCategoriesCodeMap,
    resolvableMissionTypeFileCategoriesCodeMap,
  } = useReferencesContext();

  const { values } =
    useFormikContext<ResolvableMissionTypeForm_ResolvableMissionType>();

  const { mutate: createMissionServiceUsageMutation } =
    useCreateMissionServiceUsageMutation();
  const { mutate: removeMissionServiceUsageMutation } =
    useRemoveResolvableMissionTypeServiceUsageMutation();
  const { mutate: updateMissionServiceUsageMutation } =
    useUpdateResolvableMissionTypeServiceUsageMutation();
  const { mutate: switchGroupingModeMutation } =
    useSwitchResolvableMissionTypeGroupingModeMutation();
  const { mutate: moveToGroupMutation } =
    useMoveResolvableMissionTypeServiceUsageToGroupMutation();
  const { mutate: switchTotalModeMutation } =
    useSwitchResolvableMissionTypeTotalModeMutation();
  const { mutate: updateManualTotalMutation } =
    useUpdateResolvableMissionTypeManualTotalMutation();

  const { mutate: createGroupMutation } =
    useCreateResolvableMissionTypeServiceUsagesGroupMutation();
  const { mutate: removeGroupMutation } =
    useRemoveResolvableMissionTypeServiceUsagesGroupMutation();
  const { mutate: updateGroupMutation } =
    useUpdateResolvableMissionTypeServiceUsagesGroupMutation();
  const { mutate: reorderGroupsMutation } =
    useReorderResolvableMissionTypeServiceUsagesGroupsMutation();

  const mutations = {
    groups: {
      createGroup: createGroupMutation,
      removeGroup: removeGroupMutation,
      reorderGroups: reorderGroupsMutation,
      updateGroup: updateGroupMutation,
    },
    create: createMissionServiceUsageMutation,
    moveToGroup: moveToGroupMutation,
    remove: removeMissionServiceUsageMutation,
    switchGroupingMode: switchGroupingModeMutation,
    switchTotalMode: switchTotalModeMutation,
    update: updateMissionServiceUsageMutation,
    updateManualTotal: updateManualTotalMutation,
  };

  const { data, isSuccess } = useGraphQLQuery(
    ResolvableMissionTypeForm_ServiceUsages_TotalCalculation_QueryDocument,
    {
      doc: resolvableMissionTypeForm2ApiMapper(values),
      relatedBusinessEntities: EMPTY_OBJECT,
    },
    { enabled: !useDirectMutation },
  );

  return (
    <ResolvableServiceUserFormStep
      documentResolversSettings={{
        allowedReturnTypeIds: undefined,
        enableDirectGeneration: false,
        mustBeResolvableOnBack: false,
      }}
      fallbackResolvableServiceUsagesGroups={
        fallbackResolvableServiceUsagesGroups
      }
      groupTotalCalculations={groupTotalCalculations}
      id={id}
      mutations={mutations}
      paths={paths}
      queryKeysToInvalidate={
        [
          [
            getDocumentName(
              ResolvableMissionTypePage_OneResolvableMissionType_QueryDocument,
            ),
            { id: values._id },
          ],
        ] as readonly (readonly string[])[]
      }
      resolvableFieldSettings={{
        enableDirectGeneration: false,
        primaryMode: ResolvableModeEnum.Static,
      }}
      storage={{
        directoryCategoryId:
          fileDirectoryCategoriesCodeMap.ResolvableMissionType._id,
        entityId: values._id,
        imageCategory:
          resolvableMissionTypeFileCategoriesCodeMap.TextEditorImages,
      }}
      totalCalculation={
        !totalCalculation && isSuccess
          ? data.calculateResolvableMissionTypeTotal
          : totalCalculation
      }
      useDirectMutation={useDirectMutation}
    />
  );
};
